
/**
 * Module dependencies.
 */

import {
  DisclosureName,
  useFiltersDisclosures
} from 'src/context/filters-disclosures/context';

import {
  availableFiltersKeys,
  availableFiltersValues
} from 'src/core/ambients-config/filters';

import { color, media, units } from 'src/styles/utils';
import { getKeyFromObject } from 'src/core/utils/objects';
import { useFiltersList } from 'src/context/filters-list/context';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import FiltersSidebar from './filters-sidebar';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState
} from 'react';

import camelCase from 'lodash/camelCase';
import closeIcon from 'src/assets/svg/close.svg';
import filter from 'lodash/filter';
import filterIcon from 'src/assets/svg/filter.svg';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import qs from 'qs';
import size from 'lodash/size';
import split from 'lodash/split';
import styled from 'styled-components';
import useBodyScroll from 'src/hooks/use-body-scroll';
import useProductApplications from 'src/api/app/products/use-product-applications';

/**
 * Filter buttons.
 */

const filterButtons = [
  availableFiltersKeys.application,
  availableFiltersKeys.effect
];

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  isUpdating: boolean,
  isUpdatingSucceed: boolean,
  onUpdate: () => void
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  margin-bottom: ${units(8)};
  overflow: hidden;
  position: relative;

  ${media.max('ms')`
    white-space: nowrap;

    &::after {
      background-image: linear-gradient(to right, ${color.transparentize('green400', 0)}, ${color('green400')} 100%);
      content: '';
      height: ${units(7)};
      position: absolute;
      right: 0;
      top: 0;
      width: ${units(7)};
    }
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-auto-columns: min-content;
  grid-auto-flow: column;
  grid-gap: ${units(2)};
  overflow-x: auto;
  padding-bottom: ${units(2)};
  scroll-snap-type: x mandatory;

  ${media.min('ms')`
    display: flex;
    grid-gap: 0;
    flex-flow: row wrap;
    justify-content: flex-start;
    padding-bottom: 0;

    > * {
      margin-bottom: ${units(2)};
      margin-right: ${units(2)};
    }
  `}
`;

/**
 * `useCollectionsFilters` hook.
 */

function useCollectionsFilters() {
  const { data: applications } = useProductApplications();
  const { translate } = useTranslate();
  const getFilterTranslation = useCallback((filterType: string, value: string) => {
    const keyInTranslations = camelCase(value);

    switch (filterType) {
      case availableFiltersKeys.application:
        return get(head(filter(applications, { id: value })), 'name');

      case availableFiltersKeys.effect:
        return translate(`collections:effects.${keyInTranslations}`);

      default:
        return value;
    }
  }, [applications, translate]);

  const existValue = useCallback((filterType: string, value: string) => {
    switch (filterType) {
      case availableFiltersKeys.application:
        return size(filter(applications, { id: value })) > 0;

      case availableFiltersKeys.effect:
        // @ts-ignore
        return Object.values(availableFiltersValues[filterType]).includes(value);

      default:
        return false;
    }
  }, [applications]);

  return { existValue, getFilterTranslation };
}

/**
 * `Filters` component.
 */

const Filters = (props: Props): ReactElement => {
  const { className, isUpdating, onUpdate } = props;
  const { translate } = useTranslate();
  const { existValue, getFilterTranslation } = useCollectionsFilters();
  const router = useRouter();
  const [isOpen, setOpen] = useState<boolean>();
  const [forceUpdate, setForceUpdate] = useState<boolean>();
  const { omitUrl, onSubmitRemove, submittedFilters } = useFiltersList();
  const { onCloseAll, onOpenAndCloseOthers } = useFiltersDisclosures();
  const handleOpenFilter = useCallback((name: DisclosureName) => {
    onOpenAndCloseOthers(name);
    setOpen(true);
  }, [onOpenAndCloseOthers]);

  const handleRemoveFilter = useCallback((filterType, value) => {
    const { submittedFilters } = onSubmitRemove(filterType, value);

    if (omitUrl) {
      setForceUpdate(true);

      return;
    }

    router.push({
      pathname: router.pathname,
      query: qs.stringify({ filter: submittedFilters })
    }, undefined, { shallow: true }).then(onUpdate);
  }, [omitUrl, onSubmitRemove, onUpdate, router]);

  useBodyScroll({ off: isOpen });

  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        onCloseAll();
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isOpen, onCloseAll]);

  useEffect(() => {
    if (isOpen) {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.which === 27) {
          setOpen(false);
        }
      };

      document.addEventListener('keydown', handleEscKey, false);

      return () => {
        document.removeEventListener('keydown', handleEscKey, false);
      };
    }
  }, [isOpen, setOpen]);

  useEffect(() => {
    if (omitUrl && forceUpdate) {
      const timeout = setTimeout(() => {
        onUpdate();
        setForceUpdate(false);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [forceUpdate, omitUrl, onUpdate]);

  return (
    <>
      <Wrapper className={className}>
        <Grid>
          <Button
            colorTheme={'white'}
            icon={filterIcon}
            onClick={() => setOpen(true)}
            variant={'outlined'}
          >
            {translate('common:labels.filter')}
          </Button>

          {isEmpty(submittedFilters) ? filterButtons.map(filterKey => (
            <Button
              colorTheme={'white'}
              key={filterKey}
              onClick={() => handleOpenFilter(filterKey)}
              variant={'outlined'}
            >
              {translate(`common:labels.${getKeyFromObject(availableFiltersKeys, filterKey)}`)}
            </Button>
          )) : Object
            .entries(submittedFilters)
            .map(([filterType, filterValue]) =>
              split(filterValue, ',').map((value: string) => {
                return existValue(filterType, value) && (
                  <Button
                    colorTheme={'white'}
                    icon={closeIcon}
                    key={value}
                    onClick={() => handleRemoveFilter(filterType, value)}
                    variant={'outlined'}
                  >
                    {getFilterTranslation(filterType, value)}
                  </Button>
                );
              }))}
        </Grid>
      </Wrapper>

      <FiltersSidebar
        isOpen={isOpen}
        isUpdating={isUpdating}
        onClose={() => setOpen(false)}
        onUpdate={onUpdate}
      />
    </>
  );
};

/**
 * Export `Filters` component.
 */

export default Filters;
